import { User } from '@ordermentum/ui-types';
import useSWR from 'swr';
import { fetcher } from '../../swr/fetchers';
import AuthClient from '../../utilities/auth_client';
import { HookOptionsTypes } from '../../types/HookOptionsTypes';

/**
 * Fetches the currently authenticated user.
 *
 * Used for user context, permissions, and user-specific data throughout admin app.
 * Automatically determines user ID from auth client.
 *
 * @param options - Optional SWR hook options
 * @param options.skip - Skip fetching if true
 * @returns SWR response with current user data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data: user, isLoading } = useCurrentUser({ skip: false });
 *
 * if (isLoading) return <Loading />;
 * if (!user) return <NotAuthenticated />;
 *
 * return <div>Welcome, {user.firstName}</div>;
 * ```
 */
const useCurrentUser = ({ skip = false, ...rest }: HookOptionsTypes = {}) =>
  useSWR<User>(
    () => {
      const auth = AuthClient.getAuth();
      const shouldSkip = !(auth && auth.id) || skip;
      return shouldSkip ? null : `/v1/users/${auth.id}`;
    },
    fetcher(),
    rest
  );

export default useCurrentUser;
